/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Form, Input, Row, Select, Space } from 'antd';

// Misc imports
import hasLiveChatAccess from '../../utils/misc/hasLiveChatAccess';
import extractMsTeamsDataFromUrl from '../../utils/misc/extractMsTeamsDataFromUrl';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';
import { useCreateGroupMutation, useUpdateGroupMutation } from '../../store/groups/groupsApi';
import { BOT_DEPLOYMENTS_CHANNELS, PLATFORMS } from '../../constants/main';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';

const UserGroupForm = ({
  isUpdateMode = false,
  initialGroup,
  onSubmitSuccess = () => {},
  onCancel = () => {},
  bodyClassName,
  footerClassName,
  ...restProps
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();

  const { data: botDeployments } = getCurrentQueryState('fetchBotDeployments', { orgId, tenantId });
  const { data: usersThinList } = useFetchUsersThinListQuery({ orgId });
  const [updateGroup, { isLoading: isUpdatingGroup }] = useUpdateGroupMutation();
  const [createGroup, { isLoading: isCreatingGroup }] = useCreateGroupMutation();
  const [groupType, setGroupType] = useState(form ? form.getFieldValue('platform') : null);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    if (botDeployments) {
      let platforms = [];

      platforms = Array.from(new Set(botDeployments?.items?.map((item) => {
        return item.platform;
      })));

      // Only show the platforms that are supported
      platforms = Array.from(new Set(botDeployments?.items?.map(item => item.platform)))
        .filter(platform =>
          [
            BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_TEAMS.key,
            BOT_DEPLOYMENTS_CHANNELS.SLACK.key,
            BOT_DEPLOYMENTS_CHANNELS.VIBER.key,
            BOT_DEPLOYMENTS_CHANNELS.FACEBOOK.key
          ].includes(platform)
        );

      setPlatforms(platforms);
    }
  }, [botDeployments]);

  useEffect(() => {
    form.resetFields();
    if (initialGroup?.platform) {
      setGroupType(initialGroup?.platform);
    } else {
      setGroupType(PLATFORMS.TEAMS.key);
    }

    return () => {
      form.resetFields();
    };
  }, [initialGroup]);

  const submitGroups = async (valuesToSubmit) => {
    let isSaveSuccessful = false;
    if (isUpdateMode && initialGroup) {
      // Update existing Group
      isSaveSuccessful = await updateGroup({ orgId, tenantId, groupId: initialGroup.id, data: valuesToSubmit }).unwrap();
    } else {
      // Create new Group
      isSaveSuccessful = await createGroup({ orgId, tenantId, data: valuesToSubmit }).unwrap();
    }

    if (isSaveSuccessful) {
      // Trigger callback to notify parent component of success
      onSubmitSuccess();
    }
  };

  const onSubmit = async (values) => {
    // Construct the submit values
    const valuesToSubmit = {
      ...form.getFieldsValue('addUserGroupForm'),
      tenantId: tenantId,
      active: true
    };

    submitGroups(valuesToSubmit);
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Form
          onFinish={onSubmit}
          name="audienceForm"
          layout="horizontal"
          form={form}
          initialValues={initialGroup ? {
            ...initialGroup
          } : {
            usersIds: []
          }}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          labelAlign="left"
          {...restProps}
        >
          <Form.Item
            label={t('Name')}
            name="label"
            rules={[{
              required: true,
              message: t('Please enter a name.')
            }]}
          >
            <Input placeholder={t('Type the name of the group')}/>
          </Form.Item>

          <Form.Item
            label={t('Channel')}
            name="platform"
            rules={[{
              required: true,
              message: `${t('Please select a channel')}.`
            }]}
          >
            <Select
              placeholder={t('Select a platform')}
              disabled={initialGroup}
              allowClear
              onChange={(value) => {
                setGroupType(value);
              }}
              getPopupContainer={triggerNode => triggerNode.parentElement}
            >
              {
                platforms?.map((platform) => {
                  return (
                    <Select.Option key={platform} value={platform}>
                      <Space size={4}>
                        {BOT_DEPLOYMENTS_CHANNELS[platform].icon} {t(platform, { context: 'platform type' })}
                      </Space>
                    </Select.Option>
                  );
                })
              }
            </Select>
          </Form.Item>

          <Form.Item
            label={t('Handle')}
            name="handle"
            required
            dependencies={['platform']}
            rules={[
              {
                validator: async (_rule, value) => {
                  if (groupType === PLATFORMS.TEAMS.key) {
                    return extractMsTeamsDataFromUrl(value).then(result => {
                      form.setFieldsValue({ handle: result.channelId || form.getFieldValue('handle') });

                      return Promise.resolve();
                    }).catch((err) => {
                      return Promise.reject(new Error(err.channelId || t('Please make sure you entered everything correctly.')));
                    });
                  } else {
                    return Promise.resolve();
                  }
                }
              }
            ]}
          >
            <Input placeholder={t('Enter channel URL or ID')}/>
          </Form.Item>

          {
            groupType === PLATFORMS.LIVECHAT.key ? (
              <Form.Item
                label={t('Users')}
                name="usersIds"
                rules={[{
                  required: true,
                  message: t('Please enter at least one user.')
                }]}
              >
                <Select
                  mode="tags"
                  placeholder={t('Add users')}
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                >
                  {
                    usersThinList?.filter(user => hasLiveChatAccess(user, orgId)).map((user) => (
                      <Select.Option key={user.id} value={user.id}>{user.fullName}</Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
            ) : null
          }
        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isCreatingGroup || isUpdatingGroup}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={isCreatingGroup || isUpdatingGroup}
              size="large"
            >
              {isUpdateMode ? t('Save Changes') : t('Create Audience')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

UserGroupForm.propTypes = {
  isUpdateMode: PropTypes.bool,
  initialGroup: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default UserGroupForm;
